import { Inject, Injectable } from '@angular/core';
import { PlanId } from '@app/shared/+state/billing';
import { EntitlementEnum, User } from '@app/shared/interfaces';
import { Store, select } from '@ngrx/store';
import { LoadingState } from '@ui';
import { USER_START_OF_WEEK$ } from '@ui/injection-tokens';
import { determineStartOfWeekPreference } from '@utils';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import * as UsersActions from './users.actions';
import * as UsersSelectors from './users.selectors';

@Injectable()
export class UsersFacade {
  /**
   * Combine pieces of state using createSelector,
   * and expose them as observables through the facade.
   */
  userLoadingState$ = this.store.pipe(select(UsersSelectors.getUserLoadingState));
  user$: Observable<User | null> = this.store.pipe(select(UsersSelectors.getUser));
  hasBetaFeaturesEnabled$: Observable<boolean> = this.store.pipe(select(UsersSelectors.hasBetaFeaturesEnabled));
  isInternalHospitableHost$ = this.store.pipe(select(UsersSelectors.isInternalHospitableHost));

  userTrialConnectAccountCta$ = this.store.pipe(select(UsersSelectors.getUserTrialConnectAccountCta));

  signUpWithAirbnbLoadingState$ = this.store.pipe(select(UsersSelectors.getSignUpWithAirbnbLoadingState));
  signUpWithAirbnbError$ = this.store.pipe(select(UsersSelectors.getSignUpWithAirbnbError));

  isUpsellsEnabled$ = this.user$.pipe(map((user) => user?.upsells_feature.can_access_upsells));

  /* Entitlements */
  entitlementForFeature$ = (feature: EntitlementEnum) =>
    this.userLoadingState$.pipe(
      filter((state) => state === LoadingState.Success || state === LoadingState.Error),
      take(1),
      switchMap((loadingResult) => {
        if (loadingResult === LoadingState.Error) {
          return of({
            entitlement: feature,
            available: false,
            value: '',
            current_value: null,
            unlimited: false,
          });
        }
        return this.store.select(UsersSelectors.getEntitlementForFeature(feature));
      })
    );

  public mogulBaseEntitlement$ = this.entitlementForFeature$(EntitlementEnum.MogulBase);
  public accountingEntitlement$ = this.entitlementForFeature$(EntitlementEnum.Accounting);
  public ownerPortalEntitlement$ = this.entitlementForFeature$(EntitlementEnum.OwnerPortal);
  public mogulAiEntitlement$ = this.entitlementForFeature$(EntitlementEnum.MogulAI);
  public propertiesEntitlement$ = this.entitlementForFeature$(EntitlementEnum.Properties);
  public currentPlanId$ = this.store.pipe(select(UsersSelectors.getCurrentPlan));
  public parentChildEntitlement$ = this.entitlementForFeature$(EntitlementEnum.ParentChild);
  public embeddedPricingEntitlement$ = this.entitlementForFeature$(EntitlementEnum.EmbeddedPricing);

  public isOnMogulPlan$ = this.user$.pipe(map((user) => user?.plan === PlanId.Mogul));

  public useChameleonOnboarding$ = this.user$.pipe(map((user) => user?.chameleon?.use_chameleon_onboarding));
  public startOfWeekPref$ = this.userStart.asObservable();

  public isUser31280$ = this.user$.pipe(
    filter((user) => Boolean(user)),
    map((user) => user?.team?.id === 31280)
  );

  isOnLegacyPlan$ = this.user$.pipe(map((user) => user?.plan === PlanId.Legacy));

  constructor(
    private readonly store: Store,
    @Inject(USER_START_OF_WEEK$) private userStart: BehaviorSubject<number>
  ) {
    this.user$
      .pipe(
        filter((user) => Boolean(user)),
        map((user) => determineStartOfWeekPreference(user?.week_starts_on))
      )
      .subscribe((startOfWeek) => {
        this.userStart.next(startOfWeek);
      });
  }

  loadUser() {
    this.store.dispatch(UsersActions.loadUser());
  }

  signUpWithAirbnb() {
    this.store.dispatch(UsersActions.signUpWithAirbnb());
  }

  signUpWithAirbnbCallback(code: string, state: string) {
    this.store.dispatch(UsersActions.signUpWithAirbnbCallback({ code, state }));
  }

  resetStore() {
    this.store.dispatch(UsersActions.resetStore());
  }
}
